// Client-side video compressor. Uses MediaRecorder over a canvas draw loop
// to re-encode any video into a VP9/VP8 WebM at a target bitrate so the
// resulting file lands under the given size cap.
//
// Fallback behaviour: if MediaRecorder isn't available or the browser can't
// captureStream from an HTMLVideoElement, we return the original file so the
// upload path still works (and the backend will surface its own size error).
//
// Used by Activities.jsx before POSTing to /api/activities/upload-video.

export async function compressVideoTo10MB(file, {
  targetBytes = 9.5 * 1024 * 1024,   // aim just below 10 MB
  maxDim = 720,                       // downscale to 720p max
  minBitrate = 400_000,               // 400 kbps floor
  maxBitrate = 2_500_000,             // 2.5 Mbps ceiling
  onProgress = null,
} = {}) {
  if (!file || !file.type?.startsWith("video/")) throw new Error("Not a video");

  // Small enough already? Skip re-encode.
  if (file.size <= targetBytes) return file;

  // Feature-detect. Without captureStream + MediaRecorder we can't transcode
  // client-side, so hand the original file back and let the caller warn.
  const canRecord = typeof MediaRecorder !== "undefined";
  if (!canRecord) return file;

  const video = document.createElement("video");
  video.src = URL.createObjectURL(file);
  video.muted = true;                     // required for autoplay in most browsers
  video.playsInline = true;
  video.preload = "auto";

  await new Promise((resolve, reject) => {
    video.onloadedmetadata = resolve;
    video.onerror = () => reject(new Error("Could not read video metadata"));
  });

  if (typeof video.captureStream !== "function") {
    URL.revokeObjectURL(video.src);
    return file;
  }

  const duration = Math.max(1, video.duration || 1);
  // Target bitrate = (max bytes * 8) / duration seconds, clamped.
  const targetBitrate = Math.max(minBitrate, Math.min(maxBitrate,
    Math.floor((targetBytes * 8) / duration)));

  // Scale to maxDim on the long side.
  const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
  const w = Math.max(2, Math.round(video.videoWidth * scale) & ~1);   // even numbers
  const h = Math.max(2, Math.round(video.videoHeight * scale) & ~1);

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");

  // captureStream can be on canvas OR video element — canvas gives us the
  // downscale for free. Merge with silent audio if the file had audio so the
  // encoder emits an audio track too.
  const canvasStream = canvas.captureStream(30);
  try {
    const videoStream = video.captureStream ? video.captureStream() : null;
    if (videoStream) {
      const audio = videoStream.getAudioTracks?.() || [];
      audio.forEach(t => canvasStream.addTrack(t));
    }
  } catch { /* audio track optional */ }

  // Prefer VP9, fall back to VP8, then default WebM.
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  const mime = candidates.find(m => MediaRecorder.isTypeSupported(m)) || "";
  const recorder = new MediaRecorder(canvasStream, mime ? {
    mimeType: mime, videoBitsPerSecond: targetBitrate,
  } : { videoBitsPerSecond: targetBitrate });

  const chunks = [];
  recorder.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);

  const done = new Promise((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = (e) => reject(e.error || new Error("Recorder failed"));
  });

  // Draw loop: paint the video frame onto canvas at ~30 fps until playback ends.
  let raf = 0;
  const paint = () => {
    ctx.drawImage(video, 0, 0, w, h);
    if (onProgress && duration) onProgress(Math.min(1, (video.currentTime || 0) / duration));
    raf = requestAnimationFrame(paint);
  };

  try {
    await video.play();
    recorder.start(200); // 200ms chunks
    paint();

    await new Promise((resolve) => {
      video.onended = resolve;
      // Safety timeout: 2× duration in ms (in case onended never fires).
      setTimeout(resolve, Math.min(10 * 60 * 1000, (duration + 5) * 1000 * 2));
    });
    cancelAnimationFrame(raf);
    recorder.stop();
    await done;
  } finally {
    URL.revokeObjectURL(video.src);
    video.remove();
    canvas.remove();
  }

  const blob = new Blob(chunks, { type: mime || "video/webm" });
  const outName = (file.name || "video").replace(/\.[^.]+$/, "") + ".webm";
  return new File([blob], outName, { type: blob.type });
}
